import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Check, AlertCircle, Car, FileText } from "lucide-react";
import { API, useRoleHome } from "@/contexts/AuthContext";
import Header from "@/components/shared/Header";
import IntakeContextPanel from "@/components/intake/IntakeContextPanel";

// Helper function to extract error messages
const getErrorMessage = (err, defaultMsg = "An error occurred") => {
  if (err?.response?.data?.detail) {
    const detail = err.response.data.detail;
    if (Array.isArray(detail)) {
      return detail.map(e => e.msg || e.message || JSON.stringify(e)).join(", ");
    }
    return detail;
  }
  return err?.message || defaultMsg;
};

// Lock Warning Banner Component
const LockWarningBanner = ({ lockedByName, lockedAt, onDismiss }) => {
  const formatTime = (dateStr) => {
    if (!dateStr) return "";
    const date = new Date(dateStr);
    return date.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" });
  };
  
  return (
    <div className="mb-4 p-3 bg-amber-100 border border-amber-300 rounded-lg">
      <div className="flex justify-between items-start">
        <div className="flex items-start gap-2">
          <AlertCircle className="w-5 h-5 text-amber-600 mt-0.5" />
          <div>
            <p className="font-medium text-amber-800">Job Currently Being Edited</p>
            <p className="text-sm text-amber-700">
              {lockedByName} started working on this job at {formatTime(lockedAt)}
            </p>
          </div>
        </div>
        <Button variant="ghost" size="sm" onClick={onDismiss} className="text-amber-600">
          Dismiss
        </Button>
      </div>
    </div>
  );
};

// iter213c-fix / iter213d — Post-VI-submission router. Consults the
// shared /api/tech/job/{id}/required-work endpoint (single source of
// truth for outstanding-workflow computation and canonical start
// routes) and:
//   * outstanding_count == 0 → show the historic "Inspection
//     Submitted" screen (nothing else to do)
//   * outstanding_count == 1 → jump DIRECTLY to that workflow's
//     backend-authoritative start_url (previously we always bounced
//     to /required-work, which for a single-outstanding job was an
//     extra unnecessary click and — worse — for Diagnostic caused a
//     redirect loop because /start bounces here again).
//   * outstanding_count >= 2 → open the Required Work chooser.
const PostVISubmissionRouter = ({ jobId, homeHref, onFallback }) => {
  const navigate = useNavigate();
  const [decision, setDecision] = useState("loading");   // loading | done | error
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await axios.get(`${API}/tech/job/${jobId}/required-work`);
        if (cancelled) return;
        const remaining = r.data?.outstanding_count || 0;
        if (remaining === 0) {
          setDecision("done");
          return;
        }
        if (remaining === 1) {
          const only = (r.data?.workflows || []).find((w) => w.outstanding);
          const target = only?.start_url || `/app/tech/job/${jobId}/required-work`;
          navigate(target, { replace: true });
          return;
        }
        navigate(`/app/tech/job/${jobId}/required-work`, { replace: true });
      } catch (e) {
        if (!cancelled) setDecision("error");
      }
    })();
    return () => { cancelled = true; };
  }, [jobId, navigate]);

  if (decision === "loading") return <div className="p-8 text-center">Loading...</div>;
  return (
    <div className="min-h-screen bg-gray-100">
      <Header />
      <div className="p-4 max-w-md mx-auto">
        <Card>
          <CardContent className="p-6 text-center">
            <Check className="w-12 h-12 text-green-500 mx-auto mb-4" />
            <h2 className="text-xl font-semibold">Inspection Submitted</h2>
            <p className="text-gray-600 mt-2">This job has already been completed.</p>
            <Button className="mt-4" onClick={onFallback} data-testid="back-to-jobs-btn">
              Back to Jobs
            </Button>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

// Custom hook for job locking
const useJobLock = (jobId, context, enabled = true) => {
  const [lockState, setLockState] = useState({
    isLocked: false,
    isCurrentUser: false,
    lockedByName: null,
    lockedAt: null,
    loading: true
  });

  useEffect(() => {
    if (!enabled || !jobId) {
      setLockState(prev => ({ ...prev, loading: false }));
      return;
    }

    const checkLock = async () => {
      try {
        const res = await axios.get(`${API}/jobs/${jobId}/lock`);
        setLockState({
          isLocked: res.data.is_locked,
          isCurrentUser: res.data.is_current_user,
          lockedByName: res.data.locked_by_name,
          lockedAt: res.data.locked_at,
          loading: false
        });
      } catch (err) {
        setLockState(prev => ({ ...prev, loading: false }));
      }
    };

    checkLock();
    const interval = setInterval(checkLock, 30000); // Check every 30s
    return () => clearInterval(interval);
  }, [jobId, enabled]);

  return lockState;
};

const StartInspectionPage = () => {
  const { id } = useParams();
  const [job, setJob] = useState(null);
  const [mileage, setMileage] = useState("");
  const [transmission, setTransmission] = useState("");
  const [timingSystem, setTimingSystem] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [existingInspection, setExistingInspection] = useState(null);
  const [lockWarningDismissed, setLockWarningDismissed] = useState(false);
  const navigate = useNavigate();
  // iter193 — role-aware home so FOH/ADMIN exits land on the FOH
  // dashboard, not the technician one.
  const homeHref = useRoleHome();
  
  // Job Lock - acquire lock when starting inspection
  const {
    isLocked: isJobLocked,
    isCurrentUser: hasLock,
    lockedByName,
    lockedAt,
    loading: lockLoading
  } = useJobLock(id, 'tech-start-inspection', !!id);

  useEffect(() => {
    Promise.all([
      axios.get(`${API}/jobs/${id}`),
      axios.get(`${API}/jobs/${id}/inspection`)
    ]).then(([jobRes, inspRes]) => {
      setJob(jobRes.data);
      setTransmission(jobRes.data.transmission || "");
      setTimingSystem(jobRes.data.timing_system || "");
      const prefillMileage = jobRes.data.mileage || jobRes.data.diagnostic_mileage;
      if (prefillMileage && !inspRes.data?.mileage) {
        setMileage(prefillMileage.toString());
      }
      if (inspRes.data) {
        setExistingInspection(inspRes.data);
        setMileage(inspRes.data.mileage?.toString() || jobRes.data.mileage?.toString() || jobRes.data.diagnostic_mileage?.toString() || "");
        if (inspRes.data.transmission) {
          setTransmission(inspRes.data.transmission);
        }
      }
    }).finally(() => setLoading(false));
  }, [id]);

  const handleStartVI = async (e) => {
    e.preventDefault();
    if (!mileage || parseInt(mileage) <= 0) {
      setError("Please enter a valid mileage");
      return;
    }
    setError("");
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/jobs/${id}/inspection/start`, { 
        mileage: parseInt(mileage),
        transmission: transmission || undefined,
        timing_system: timingSystem || undefined
      });
      
      // Check job-level arrival photos (shared between Diagnostic VI and VI)
      const photosRes = await axios.get(`${API}/jobs/${id}/arrival-photos`);
      const photoCount = photosRes.data?.length || 0;
      
      if (photoCount >= 4) {
        navigate(`/app/tech/job/${id}/sections`);
      } else {
        navigate(`/app/tech/job/${id}/arrival`, { state: { inspectionId: res.data.id } });
      }
    } catch (err) {
      setError(getErrorMessage(err, "Failed to start inspection"));
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return <div className="p-8 text-center">Loading...</div>;

  // iter213c-fix — When the S&R Inspection has been submitted but the
  // job still has an outstanding MOT or Diagnostic workflow, don't
  // dead-end on "Inspection Submitted / Back to Jobs". Consult the
  // shared /api/tech/job/{id}/required-work endpoint (single source
  // of truth) and, if anything remains outstanding, bounce to the
  // Required Work chooser so the tech can pick up the next task.
  if (existingInspection?.status === "submitted") {
    return (
      <PostVISubmissionRouter
        jobId={id}
        homeHref={homeHref}
        onFallback={() => navigate(homeHref)}
      />
    );
  }

  const needsDiagnostic = job?.requires_diagnostic_vi && job?.diagnostic_status !== 'completed';
  const needsVI = job?.requires_vehicle_inspection && job?.vi_status !== 'completed';
  const diagnosticComplete = job?.requires_diagnostic_vi && job?.diagnostic_status === 'completed';

  return (
    <div className="min-h-screen bg-gray-100">
      <Header />
      <div className="p-4 max-w-lg mx-auto">
        {isJobLocked && !hasLock && !lockWarningDismissed && (
          <LockWarningBanner 
            lockedByName={lockedByName}
            lockedAt={lockedAt}
            onDismiss={() => setLockWarningDismissed(true)}
          />
        )}

        {(job?.gh_job_ref || job?.customer_name || job?.customer_phone || job?.transmission || job?.key_tag_number) && (
          <Card className="mb-4 border-blue-200 bg-blue-50">
            <CardContent className="p-4">
              <h3 className="text-sm font-semibold text-blue-800 mb-2">Job Details</h3>
              <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-sm">
                {job?.gh_job_ref && (
                  <>
                    <span className="text-slate-500">Job Ref:</span>
                    <span className="font-medium text-slate-700">{job.gh_job_ref}</span>
                  </>
                )}
                {job?.customer_name && (
                  <>
                    <span className="text-slate-500">Customer:</span>
                    <span className="font-medium text-slate-700">{job.customer_name}</span>
                  </>
                )}
                {job?.customer_phone && (
                  <>
                    <span className="text-slate-500">Phone:</span>
                    <span className="font-medium text-slate-700">{job.customer_phone}</span>
                  </>
                )}
                {job?.key_tag_number && (
                  <>
                    <span className="text-slate-500">Key Tag:</span>
                    <span className="font-bold text-amber-700 bg-amber-100 px-2 py-0.5 rounded">{job.key_tag_number}</span>
                  </>
                )}
                {job?.transmission && (
                  <>
                    <span className="text-slate-500">Transmission:</span>
                    <span className="font-medium text-slate-700">{job.transmission}</span>
                  </>
                )}
                {job?.fuel_type && (
                  <>
                    <span className="text-slate-500">Fuel:</span>
                    <span className="font-medium text-slate-700">{job.fuel_type}</span>
                  </>
                )}
              </div>
            </CardContent>
          </Card>
        )}

        {/* FOH Intake Context — surfaced inline when this job was created from
            a Yestimate Intake session. Collapsed by default. */}
        <IntakeContextPanel intakeSessionId={job?.intake_session_id} />

        {/* NOTE: Customer Intake Summary is intentionally NOT rendered on
            the technician dashboard. It's an FOH-only surface — the verbatim
            "Read aloud" recap, retention steers and FOH-only watch-outs are
            tools for the booking call and don't belong in the technician
            workflow. Tech context comes from IntakeContextPanel above and
            the Tech / Tester Intel packs. */}

        <Card data-testid="start-inspection-card">
          <CardHeader>
            <div className="flex items-center gap-3 mb-2">
              <img 
                src="/yestimate-logo.jpeg" 
                alt="Yestimate" 
                className="h-10 w-auto opacity-70"
                style={{ maxHeight: '40px' }}
              />
              <span className="text-xs text-slate-400">Inspection System</span>
            </div>
            <CardTitle>Inspection Workflow</CardTitle>
            <CardDescription>{job?.reg} - {job?.make} {job?.model}</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {error && (
              <div className="p-3 bg-red-100 text-red-700 rounded flex items-center gap-2" data-testid="start-error">
                <AlertCircle className="w-4 h-4" />
                {error}
              </div>
            )}

            <div className="p-3 bg-slate-50 rounded-lg">
              <p className="text-sm font-medium text-slate-600 mb-2">Required Inspections:</p>
              <div className="space-y-1 text-sm">
                {job?.requires_diagnostic_vi && (
                  <div className="flex items-center gap-2">
                    {diagnosticComplete ? (
                      <Check className="w-4 h-4 text-green-500" />
                    ) : (
                      <div className="w-4 h-4 border-2 border-purple-500 rounded" />
                    )}
                    <span className={diagnosticComplete ? 'text-green-700' : 'text-purple-700'}>
                      Diagnostic Inspection {diagnosticComplete && '(Complete)'}
                    </span>
                  </div>
                )}
                {job?.requires_vehicle_inspection && (
                  <div className="flex items-center gap-2">
                    {existingInspection?.status === 'submitted' ? (
                      <Check className="w-4 h-4 text-green-500" />
                    ) : (
                      <div className="w-4 h-4 border-2 border-blue-500 rounded" />
                    )}
                    <span className={existingInspection?.status === 'submitted' ? 'text-green-700' : 'text-blue-700'}>
                      Safety & Reliability Inspection
                    </span>
                  </div>
                )}
              </div>
            </div>

            {needsDiagnostic && (
              <div className="border-2 border-purple-300 bg-purple-50 rounded-lg p-4">
                <h3 className="font-semibold text-purple-800 flex items-center gap-2">
                  <FileText className="w-5 h-5" />
                  Diagnostic Inspection Required
                </h3>
                <div className="mt-3 p-3 bg-white rounded border border-purple-200">
                  <p className="text-xs text-purple-600 font-medium">CUSTOMER REPORTED CONCERN:</p>
                  <p className="text-sm text-slate-700 mt-1">{job?.customer_concern}</p>
                </div>
                
                <div className="mt-4 p-3 bg-blue-50 rounded border border-blue-200">
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label htmlFor="diag-mileage-start" className="text-blue-700 font-medium">Current Mileage *</Label>
                      <Input
                        id="diag-mileage-start"
                        type="number"
                        value={mileage}
                        onChange={(e) => setMileage(e.target.value)}
                        placeholder="Enter current mileage"
                        required
                        min="1"
                        className="mt-1 bg-white"
                        data-testid="diag-mileage-start-input"
                      />
                    </div>
                    <div>
                      <Label htmlFor="diag-transmission-start" className="text-blue-700 font-medium">Transmission</Label>
                      <select
                        id="diag-transmission-start"
                        value={transmission}
                        onChange={(e) => setTransmission(e.target.value)}
                        className="mt-1 w-full p-2 border rounded-md bg-white h-10"
                        data-testid="diag-transmission-start-input"
                      >
                        <option value="">Select...</option>
                        <option value="Manual">Manual</option>
                        <option value="Automatic">Automatic (Torque Converter)</option>
                        <option value="DSG">DSG / Dual Clutch</option>
                        <option value="CVT">CVT</option>
                        <option value="Automated Manual">Automated Manual</option>
                      </select>
                    </div>
                    <div>
                      <Label htmlFor="diag-timing-system-start" className="text-blue-700 font-medium">Timing System</Label>
                      <select
                        id="diag-timing-system-start"
                        value={timingSystem}
                        onChange={(e) => setTimingSystem(e.target.value)}
                        className="mt-1 w-full p-2 border rounded-md bg-white h-10"
                        data-testid="diag-timing-system-start-input"
                      >
                        <option value="">Select...</option>
                        <option value="chain">Timing Chain</option>
                        <option value="belt">Timing Belt (Cam Belt)</option>
                        <option value="wet_belt">Wet Belt (Oil Bath)</option>
                        <option value="unknown">Unknown / Not Visible</option>
                      </select>
                      <p className="text-xs text-slate-500 mt-1">Check engine bay or service book if unsure</p>
                    </div>
                  </div>
                </div>
                
                <Button 
                  className="w-full mt-4 bg-purple-600 hover:bg-purple-700"
                  onClick={async () => {
                    if (!mileage || parseInt(mileage) <= 0) {
                      setError("Please enter a valid mileage before starting diagnostic");
                      return;
                    }
                    setError("");
                    try {
                      await axios.patch(`${API}/jobs/${id}`, { 
                        mileage: parseInt(mileage),
                        transmission: transmission || undefined,
                        timing_system: timingSystem || undefined
                      });
                      navigate(`/app/tech/job/${id}/diagnostic`);
                    } catch (err) {
                      navigate(`/app/tech/job/${id}/diagnostic`);
                    }
                  }}
                  data-testid="start-diagnostic-btn"
                >
                  Start Diagnostic Capture
                </Button>
              </div>
            )}

            {diagnosticComplete && (
              <div className="border-2 border-green-300 bg-green-50 rounded-lg p-4">
                <h3 className="font-semibold text-green-800 flex items-center gap-2">
                  <Check className="w-5 h-5" />
                  Diagnostic Complete
                </h3>
                <p className="text-sm text-green-700 mt-2">
                  Diagnostic findings have been submitted and processed.
                </p>
                {!job?.requires_vehicle_inspection && (
                  <div className="mt-3 p-3 bg-green-100 border border-green-300 rounded text-center">
                    <p className="text-green-800 font-semibold">✓ All Inspections Complete</p>
                    <p className="text-green-600 text-sm mt-1">Job ready for FOH to review and send to customer</p>
                  </div>
                )}
              </div>
            )}

            {needsVI && (
              <div className="border-2 border-blue-300 bg-blue-50 rounded-lg p-4">
                <h3 className="font-semibold text-blue-800 flex items-center gap-2">
                  <Car className="w-5 h-5" />
                  Safety & Reliability Inspection
                </h3>
                <form onSubmit={handleStartVI} className="mt-3 space-y-3">
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label htmlFor="mileage" className="text-blue-700">Current Mileage *</Label>
                      <Input
                        id="mileage"
                        type="number"
                        value={mileage}
                        onChange={(e) => setMileage(e.target.value)}
                        placeholder="Enter current mileage"
                        required
                        min="1"
                        className="mt-1 bg-white"
                        data-testid="mileage-input"
                      />
                      {job?.mileage && mileage === job.mileage.toString() && job?.diagnostic_status === 'completed' && (
                        <p className="text-xs text-blue-500 mt-1">Pre-filled from Diagnostic VI</p>
                      )}
                    </div>
                    <div>
                      <Label htmlFor="transmission" className="text-blue-700">Transmission</Label>
                      <select
                        id="transmission"
                        value={transmission}
                        onChange={(e) => setTransmission(e.target.value)}
                        className="mt-1 w-full p-2 border rounded-md bg-white h-10"
                        data-testid="transmission-input"
                      >
                        <option value="">Select...</option>
                        <option value="Manual">Manual</option>
                        <option value="Automatic">Automatic (Torque Converter)</option>
                        <option value="DSG">DSG / Dual Clutch</option>
                        <option value="CVT">CVT</option>
                        <option value="Automated Manual">Automated Manual</option>
                      </select>
                    </div>
                    <div>
                      <Label htmlFor="timing-system" className="text-blue-700">Timing System</Label>
                      <select
                        id="timing-system"
                        value={timingSystem}
                        onChange={(e) => setTimingSystem(e.target.value)}
                        className="mt-1 w-full p-2 border rounded-md bg-white h-10"
                        data-testid="timing-system-input"
                      >
                        <option value="">Select...</option>
                        <option value="chain">Timing Chain</option>
                        <option value="belt">Timing Belt (Cam Belt)</option>
                        <option value="wet_belt">Wet Belt (Oil Bath)</option>
                        <option value="unknown">Unknown / Not Visible</option>
                      </select>
                      <p className="text-xs text-slate-500 mt-1">Check engine bay or service book if unsure</p>
                    </div>
                  </div>
                  <Button 
                    type="submit" 
                    className="w-full bg-blue-600 hover:bg-blue-700" 
                    disabled={submitting} 
                    data-testid="continue-btn"
                  >
                    {submitting ? "Starting..." : (existingInspection && existingInspection.status !== 'submitted' ? "Continue Inspection" : "Start Safety & Reliability Inspection")}
                  </Button>
                </form>
              </div>
            )}

            <Button 
              variant="outline" 
              className="w-full" 
              onClick={() => navigate(homeHref)}
            >
              Back to Jobs
            </Button>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default StartInspectionPage;
